import { getSecureItem, removeSecureItem, setSecureItem, SECURE_AUTH_KEYS } from '@/lib/secureStorage'

type RequestOptions = { requiresAuth?: boolean; cache?: boolean; cacheTtlMs?: number }

/**
 * Shared param type for list()/query-string builders — every `.list()` override should use
 * this instead of a narrower `Record<string, string | number>`, since real filters (isActive,
 * orgMedia, etc.) are frequently booleans and would otherwise fail to typecheck against
 * createQueryParams, which already accepts them.
 */
export type QueryParams = Record<string, string | number | boolean | undefined | null>

interface CacheEntry<T = unknown> {
  expiresAt: number
  promise: Promise<T>
}

/**
 * Module-level (shared across every ApiService/subclass instance) GET response
 * cache, so repeat `.list()`/`.get()` calls within the TTL window reuse the same
 * in-flight or resolved response instead of hitting the network again. Any
 * successful non-GET request automatically invalidates cached entries for that
 * same resource, so a create/update/delete always shows up fresh on the next read.
 */
const requestCache = new Map<string, CacheEntry>()
const DEFAULT_CACHE_TTL_MS = 45_000

function resourceRoot(endpoint: string): string {
  const path = endpoint.split('?')[0]
  const segments = path.split('/').filter(Boolean)
  return `/${segments.slice(0, 2).join('/')}`
}

function invalidateResourceCache(endpoint: string): void {
  const root = resourceRoot(endpoint)
  for (const key of requestCache.keys()) {
    if (key.startsWith(`GET:${root}`)) requestCache.delete(key)
  }
}

class ApiService {
  private readonly refreshTokenEndpoint = '/api/auth/refresh'

  private get accessToken(): string | null {
    return getSecureItem('accessToken')
  }

  private get refreshToken(): string | null {
    return getSecureItem('refreshToken')
  }

  getBaseURL(): string {
    const configured = import.meta.env.VITE_APP_API_URL
    if (configured) {
      const base = configured.replace(/\/$/, '')
      // An https page can't call an http API (browser blocks it as mixed content).
      if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
        return base.replace(/^http:\/\//, 'https://')
      }
      return base
    }
    return ''
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    if (response.status === 204) {
      return null as T
    }

    const text = await response.text()
    if (!text) {
      return null as T
    }

    return JSON.parse(text) as T
  }

  async request<T = unknown>(
    endpoint: string,
    method = 'GET',
    body: object | FormData | null = null,
    options: RequestOptions = { requiresAuth: true },
  ): Promise<T> {
    const isGet = method === 'GET'
    const cacheEnabled = isGet && options.cache !== false
    const cacheKey = `GET:${endpoint}`

    if (cacheEnabled) {
      const cached = requestCache.get(cacheKey)
      if (cached && cached.expiresAt > Date.now()) {
        return cached.promise as Promise<T>
      }
    }

    const exec = async (): Promise<T> => {
      const headers = new Headers()
      const requiresAuth = options.requiresAuth !== false

      if (requiresAuth) {
        const token = this.accessToken
        if (token) {
          headers.append('Authorization', `Bearer ${token}`)
        }
      }

      const config: RequestInit = { method, headers }

      if (body instanceof FormData) {
        config.body = body
      } else if (body) {
        headers.append('Content-Type', 'application/json')
        config.body = JSON.stringify(body)
      }

      const url = `${this.getBaseURL()}${endpoint}`

      let response = await fetch(url, config)

      if (response.status === 401 && requiresAuth) {
        const refreshResponse = await this.refreshAccessToken()
        if (refreshResponse.ok) {
          const newToken = getSecureItem('accessToken')
          if (newToken) {
            headers.set('Authorization', `Bearer ${newToken}`)
          }
          response = await fetch(url, { ...config, headers })
        } else {
          this.logout()
          throw new Error('Session expired. Please log in again.')
        }
      }

      if (!response.ok) {
        const errorBody = await this.parseResponse<{ message?: string }>(response).catch(() => null)
        throw new Error(errorBody?.message || `Request failed (${response.status})`)
      }

      return this.parseResponse<T>(response)
    }

    if (!isGet) {
      const result = await exec()
      invalidateResourceCache(endpoint)
      return result
    }

    if (!cacheEnabled) {
      return exec()
    }

    const ttl = options.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS
    const promise = exec().catch((error) => {
      requestCache.delete(cacheKey)
      throw error
    })
    requestCache.set(cacheKey, { expiresAt: Date.now() + ttl, promise })
    return promise
  }

  async refreshAccessToken(): Promise<Response> {
    const refreshToken = this.refreshToken
    if (!refreshToken) {
      return new Response(null, { status: 401 })
    }

    const response = await fetch(`${this.getBaseURL()}${this.refreshTokenEndpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })

    if (response.ok) {
      const data = await response.json()
      setSecureItem('accessToken', data.accessToken)
      setSecureItem('refreshToken', data.refreshToken)
      window.dispatchEvent(new Event('authChange'))
    }

    return response
  }

  logout(): void {
    for (const key of SECURE_AUTH_KEYS) removeSecureItem(key)
    requestCache.clear()

    window.dispatchEvent(new Event('authChange'))
    window.location.href = '/auth/'
  }

  createQueryParams(json: QueryParams): string {
    return Object.entries(json)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&')
  }

  async get<T = unknown>(endpoint: string, options: RequestOptions = { requiresAuth: true }): Promise<T> {
    return this.request<T>(endpoint, 'GET', null, options)
  }

  async post<T = unknown>(
    endpoint: string,
    body: object,
    options: RequestOptions = { requiresAuth: true },
  ): Promise<T> {
    return this.request<T>(endpoint, 'POST', body, options)
  }

  async put<T = unknown>(
    endpoint: string,
    body: object,
    options: RequestOptions = { requiresAuth: true },
  ): Promise<T> {
    return this.request<T>(endpoint, 'PUT', body, options)
  }

  async patch<T = unknown>(
    endpoint: string,
    body: object,
    options: RequestOptions = { requiresAuth: true },
  ): Promise<T> {
    return this.request<T>(endpoint, 'PATCH', body, options)
  }

  async delete<T = unknown>(endpoint: string, options: RequestOptions = { requiresAuth: true }): Promise<T> {
    return this.request<T>(endpoint, 'DELETE', null, options)
  }

  /** Fetch a binary body and trigger a Save dialog. Same 401 -> refresh -> retry as request(). */
  async download(endpoint: string, fallbackFilename: string): Promise<void> {
    const url = `${this.getBaseURL()}${endpoint}`
    const authHeaders = (): Headers => {
      const headers = new Headers()
      const token = getSecureItem('accessToken')
      if (token) headers.append('Authorization', `Bearer ${token}`)
      return headers
    }

    let response = await fetch(url, { method: 'GET', headers: authHeaders() })
    if (response.status === 401) {
      const refreshed = await this.refreshAccessToken()
      if (!refreshed.ok) {
        this.logout()
        throw new Error('Session expired. Please log in again.')
      }
      response = await fetch(url, { method: 'GET', headers: authHeaders() })
    }
    if (!response.ok) {
      throw new Error(`Download failed (${response.status})`)
    }

    const disposition = response.headers.get('Content-Disposition') ?? ''
    const match = /filename="?([^"]+)"?/i.exec(disposition)
    const filename = match?.[1] ?? fallbackFilename

    const blob = await response.blob()
    const objectUrl = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    // Give the browser a tick to start the download before releasing the blob.
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0)
  }
}

export default ApiService
